/**
 * Pure logic for the session-only hand-drawn rendering toggle in the
 * diagram toolbar.
 *
 * The viewer's override is held in component state (never persisted) and
 * starts unset (`null`), meaning the diagram respects the model author's
 * default (`spec.appearance`). Clicking the toolbar button flips the look
 * relative to the *currently effective* theme — so on a diagram whose model
 * defaults to hand-drawn, the first click switches it to crisp, and vice
 * versa. Extracted from DiagramWorkspace so the flip semantics are
 * unit-testable without mounting the workspace.
 */
import type { DiagramAppearance } from "@diagram/diagram-view";

/** A concrete (non-undefined) appearance theme. */
export type AppearanceTheme = NonNullable<DiagramAppearance["theme"]>;

/**
 * The theme the viewer currently sees: the session override when set,
 * otherwise the model author's spec default, otherwise "default".
 * Mirrors the precedence DiagramRenderer applies (prop > spec > default).
 */
export function effectiveAppearanceTheme(
  override: AppearanceTheme | null,
  specAppearance: DiagramAppearance | undefined,
): AppearanceTheme {
  return override ?? specAppearance?.theme ?? "default";
}

/**
 * The override to set when the toggle button is clicked: the opposite of
 * the currently effective theme. Always returns a concrete theme (never
 * null) — once the viewer has toggled, the override pins the look for the
 * rest of the session regardless of the spec default.
 */
export function flippedAppearanceOverride(
  override: AppearanceTheme | null,
  specAppearance: DiagramAppearance | undefined,
): AppearanceTheme {
  return effectiveAppearanceTheme(override, specAppearance) === "hand-drawn"
    ? "default"
    : "hand-drawn";
}
